// @ts-nocheck
import React, { useRef, useState, useEffect } from "react";
import { AiFillDelete, AiOutlinePlus } from "react-icons/ai";
import { MdOutlineLocationCity } from "react-icons/md";
import { FaXmark } from "react-icons/fa6";
import Modal from "react-modal";
import { ImSpinner2 } from "react-icons/im";
import { useDispatch, useSelector } from "react-redux";
import { ToastContainer, toast } from "react-toastify";
import AdminVehicleClassesRate from "../rates/VehicleClasses";
import LocationInput from "../../../../ui/Form/LocationInput";

import {
  createCity,
  deleteCity,
  fetchCities,
  fetchCity,
  updateCity,
} from "../../../../../redux/slices/adminSlice";

// --- Type Configuration ---
const bookingTypeConfig = {
  airport: {
    id: 1,
    title: "Cities and Airports",
    description: "Manage cities and airports on Shuttlelane",
    cityLabel: "City",
    associatedEntitiesLabel: "Airports",
    addEntityPlaceholder: "Murtala Muhammed International Airport (MMIA 1)",
    entityDisplayNameLabel: "Airport Display Name",
    entityLocationLabel: "Airport Name",
    tab2Title: "Vehicle Classes",
    tab2Component: AdminVehicleClassesRate,
  },
  car: {
    id: 2,
    title: "Cities and Car Rental Locations",
    description: "Manage cities and car rental locations on Shuttlelane",
    cityLabel: "City",
    associatedEntitiesLabel: "Car Rental Locations",
    addEntityPlaceholder: "Lagos Car Rental Hub",
    entityDisplayNameLabel: "Location Display Name",
    entityLocationLabel: "Location Address",
    tab2Title: "Car Rates",
    // We will now pass this tab2Component from the parent (AdminCarRates)
    // as a prop, rather than defining it here.
    // So, this will remain null or simply be removed as it's overridden by props.
    tab2Component: null, // Keep this as null here, as it's meant to be passed via props
  },
  priority: {
    id: 3,
    title: "Cities and Priority Service Points",
    description: "Manage cities and priority service points on Shuttlelane",
    cityLabel: "City",
    associatedEntitiesLabel: "Service Points",
    addEntityPlaceholder: "VIP Lounge Lagos",
    entityDisplayNameLabel: "Service Point Name",
    entityLocationLabel: "Service Point Address",
    tab2Title: "Priority Pass Rates",
    tab2Component: null,
  },
};

// --- Main Component ---
// Add tab2Component to the destructured props
function AdminCitiesLayout({
  type = "airport",
  tab2Component: PassedTab2Component,
}) {
  // Renamed for clarity
  const dispatch = useDispatch();
  const { token, isLoading, cities, currentCity } = useSelector(
    (store) => store.admin
  );

  const [isOverview, setIsOverview] = useState(true);
  const [isTab2Active, setIsTab2Active] = useState(false);

  const [selectedCity, setSelectedCity] = useState(null);

  // Add City Modal State
  const [isAddCityModalOpen, setIsAddCityModalOpen] = useState(false);
  const [cityName, setCityName] = useState("");

  // Update City Modal State
  const [currentClickedCity, setCurrentClickedCity] = useState(null);
  const [isUpdateCityModalOpen, setIsUpdateCityModalOpen] = useState(false);
  const [modifiedCityName, setModifiedCityName] = useState("");
  const [modifiedAssociatedEntities, setModifiedAssociatedEntities] = useState(
    []
  );

  const [newEntityDisplayName, setNewEntityDisplayName] = useState("");
  const [newEntityLocation, setNewEntityLocation] = useState(null);
  const newEntityLocationRef = useRef(null);
  const [newEntityLocationInput, setNewEntityLocationInput] = useState("");

  // --- Effects ---
  useEffect(() => {
    dispatch(fetchCities({ token, type }));
  }, [token, type]);

  useEffect(() => {
    if (selectedCity) {
      dispatch(fetchCity({ token, cityId: selectedCity.value, type }));
    }
  }, [selectedCity, token, type]);

  useEffect(() => {
    if (currentClickedCity) {
      setModifiedCityName(currentClickedCity.cityName);
      // Ensure 'associatedEntities' is the correct field from your backend response
      setModifiedAssociatedEntities(
        currentClickedCity.associatedEntities || []
      );
    }
  }, [currentClickedCity]);

  const config = bookingTypeConfig[type];
  if (!config) {
    console.error(`Invalid booking type: ${type}`);
    return <div>Error: Invalid booking type specified.</div>;
  }

  // --- Handlers ---
  async function createNewCity(e) {
    e.preventDefault();
    if (!cityName || cityName.trim().length === 0) {
      toast.error(`You must specify a ${config.cityLabel.toLowerCase()} name!`);
      return;
    }
    dispatch(createCity({ token, cityName, type }));
    setIsAddCityModalOpen(false);
    setCityName("");
  }

  function handleAddAssociatedEntity() {
    if (newEntityDisplayName.trim() === "") {
      toast.info(
        `You must specify a ${config.entityDisplayNameLabel.toLowerCase()}.`
      );
      return;
    }

    const entityToAdd = {
      displayName: newEntityDisplayName,
      name: newEntityLocationInput,
      lng: newEntityLocation?.longitude,
      lat: newEntityLocation?.latitude,
    };

    setModifiedAssociatedEntities((prevEntities) => [
      ...prevEntities,
      entityToAdd,
    ]);
    setNewEntityDisplayName("");
    setNewEntityLocationInput("");
    setNewEntityLocation(null);
  }

  function handleRemoveAssociatedEntity(entityToRemove) {
    setModifiedAssociatedEntities((prevEntities) =>
      prevEntities.filter((entity) => entity !== entityToRemove)
    );
  }

  async function handleDeleteCity(cityId) {
    dispatch(
      deleteCity({
        cityId: cityId,
        token: token,
        type,
      })
    );
  }

  async function handleUpdateCity(e) {
    e.preventDefault();
    dispatch(
      updateCity({
        cityId: currentClickedCity?._id,
        cityName: modifiedCityName,
        associatedEntities: modifiedAssociatedEntities,
        token: token,
        type,
      })
    );
    setIsUpdateCityModalOpen(false);
  }

  // Use the PassedTab2Component if provided, otherwise fall back to config
  const Tab2ComponentToRender = PassedTab2Component || config.tab2Component;

  return (
    <div className="">
      <ToastContainer />
      {/* ... (Modals remain the same) ... */}

      {/* Add City Modal */}
      <Modal
        isOpen={isAddCityModalOpen}
        onRequestClose={() => setIsAddCityModalOpen(false)}
        className="flex h-full min-h-screen justify-center items-center lg:px-24 px-7"
      >
        <div className="bg-white shadow-lg rounded-lg text-shuttlelaneBlack lg:w-2/4 w-full p-7 px-10">
          <div className="flex items-center justify-between">
            <h4 className="font-semibold">Add New {config.cityLabel}</h4>
            <FaXmark
              size={20}
              onClick={() => setIsAddCityModalOpen(false)}
              className="cursor-pointer"
            />
          </div>

          <form className="w-full mt-5" onSubmit={createNewCity}>
            <div className="flex flex-col gap-y-5 lg:items-center gap-x-4">
              <div className="w-full flex flex-col gap-y-1">
                <label htmlFor="cityName" className="text-sm">
                  {config.cityLabel}
                </label>
                <input
                  type="text"
                  placeholder={`Enter ${config.cityLabel.toLowerCase()} name`}
                  name="cityName"
                  value={cityName}
                  onChange={(e) => setCityName(e.target.value)}
                  className="w-full text-sm h-11 p-3 border-[0.3px] bg-transparent focus:outline-none border-gray-400 rounded-lg"
                />
              </div>
              <button
                type="submit"
                disabled={isLoading}
                className="w-full flex justify-center items-center text-sm text-white hover:text-shuttlelaneBlack h-11 p-3 transition-all hover:border-[1px] hover:bg-transparent bg-shuttlelanePurple focus:outline-none border-gray-400 rounded-lg"
              >
                {isLoading ? (
                  <ImSpinner2 size={21} className="animate-spin" />
                ) : (
                  "Save"
                )}
              </button>
            </div>
          </form>
        </div>
      </Modal>

      {/* Update City Modal */}
      <Modal
        isOpen={isUpdateCityModalOpen}
        onRequestClose={() => setIsUpdateCityModalOpen(false)}
        className="flex h-full min-h-screen justify-center items-center lg:px-24 px-7"
      >
        <div className="bg-white shuttlelaneScrollbar min-h-[90vh] max-h-[90vh] h-[90vh] overflow-y-scroll shadow-lg rounded-lg text-shuttlelaneBlack lg:w-2/4 w-full p-7 px-10">
          <div className="flex items-center justify-between">
            <h4 className="font-semibold">Modify {config.cityLabel}</h4>
            <div className="flex items-center gap-2">
              <FaXmark
                size={20}
                onClick={() => setIsUpdateCityModalOpen(false)}
                className="cursor-pointer"
              />
            </div>
          </div>

          <form className="w-full mt-5" onSubmit={handleUpdateCity}>
            <div className="flex flex-col gap-y-5 lg:items-center gap-x-4">
              <div className="w-full flex flex-col">
                <label htmlFor="modifiedCityName" className="text-sm">
                  {config.cityLabel} Name
                </label>
                <input
                  type="text"
                  placeholder={`Enter ${config.cityLabel.toLowerCase()} name`}
                  name="modifiedCityName"
                  value={modifiedCityName}
                  onChange={(e) => setModifiedCityName(e.target.value)}
                  className="w-full text-sm h-11 p-3 border-[0.3px] bg-transparent focus:outline-none border-gray-400 rounded-lg"
                />
              </div>
              {config.id === 1 && (
                <div className="w-full flex flex-col">
                  <label
                    htmlFor="modifiedAssociatedEntities"
                    className="text-sm"
                  >
                    {config.associatedEntitiesLabel}
                  </label>
                  <div className="flex items-center flex-wrap gap-4 my-2">
                    {modifiedAssociatedEntities?.map((entity, index) => (
                      <div
                        key={index}
                        className="bg-shuttlelanePurple text-white h-10 p-2 flex items-center justify-between rounded-md"
                      >
                        <span className="text-sm">{entity?.displayName}</span>
                        <FaXmark
                          size={16}
                          onClick={() => handleRemoveAssociatedEntity(entity)}
                          className="cursor-pointer ml-2"
                        />
                      </div>
                    ))}
                    {modifiedAssociatedEntities?.length < 1 && (
                      <div className="border-[2px] border-gray-300 border-dashed text-gray-300 h-10 p-2 flex items-center justify-between rounded-md">
                        <span className="text-sm">
                          Add {config.associatedEntitiesLabel.toLowerCase()}
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col gap-y-3 my-4 border-[3px] border-dashed border-gray-300 rounded-lg p-5">
                    <div className="w-full flex flex-col gap-y-1">
                      <label htmlFor="newEntityDisplayName" className="text-xs">
                        {config.entityDisplayNameLabel}
                      </label>
                      <input
                        type="text"
                        placeholder={config.addEntityPlaceholder}
                        name="newEntityDisplayName"
                        value={newEntityDisplayName}
                        onChange={(e) =>
                          setNewEntityDisplayName(e.target.value)
                        }
                        className="w-full text-sm h-11 p-3 border-[0.3px] bg-transparent focus:outline-none border-gray-400 rounded-lg"
                      />
                    </div>
                    <div className="w-full flex flex-col gap-y-1">
                      <label htmlFor="newEntityLocation" className="text-xs">
                        {config.entityLocationLabel}
                      </label>
                      <div className="flex items-center">
                        <div className="relative w-[85%] text-sm h-11 p-3 border-[0.3px] bg-transparent focus:outline-none border-gray-400 rounded-tl-lg rounded-bl-lg">
                          <LocationInput
                            placeholder={`Eg. ${config.entityLocationLabel}`}
                            setLocation={setNewEntityLocation}
                            location={newEntityLocation}
                            locationRef={newEntityLocationRef}
                            locationInput={newEntityLocationInput}
                            setLocationInput={setNewEntityLocationInput}
                          />
                        </div>

                        <button
                          type="button"
                          disabled={isLoading}
                          onClick={handleAddAssociatedEntity}
                          className="w-[15%] flex justify-center items-center text-sm text-white hover:text-shuttlelaneBlack h-11 p-3 transition-all hover:border-[1px] hover:bg-transparent bg-shuttlelanePurple focus:outline-none border-gray-400 rounded-tr-lg rounded-br-lg"
                        >
                          Add
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              <button
                type="submit"
                disabled={isLoading}
                className="w-full flex justify-center items-center text-sm text-white hover:text-shuttlelaneBlack h-11 p-3 transition-all hover:border-[1px] hover:bg-transparent bg-shuttlelanePurple focus:outline-none border-gray-400 rounded-lg"
              >
                {isLoading ? (
                  <ImSpinner2 size={21} className="animate-spin" />
                ) : (
                  "Save"
                )}
              </button>
            </div>
          </form>
        </div>
      </Modal>

      {/* <h2 className="font-semibold capitalize text-xl text-shuttlelaneBlack">
        {config.title} - {type} bookings
      </h2>
      <p className="text-sm">{config.description}</p> */}

      {/* Options */}
      <div className="mt-4 flex justify-between items-baseline pb-1 transition-all border-b-[.3px] border-b-gray-200">
        <div className="flex items-center gap-x-10">
          <span
            onClick={() => {
              setIsOverview(true);
              setIsTab2Active(false);
              setSelectedCity(null);
            }}
            className={`text-xs cursor-pointer transition-all ${
              isOverview
                ? "font-semibold text-shuttlelaneBlack border-b-2 border-b-shuttlelaneBlack"
                : "text-gray-400"
            }`}
          >
            Overview
          </span>
          <span
            onClick={() => {
              setIsOverview(false);
              setIsTab2Active(true);
            }}
            className={`text-xs cursor-pointer transition-all ${
              isTab2Active
                ? "font-semibold text-shuttlelaneBlack border-b-2 border-b-shuttlelaneBlack"
                : "text-gray-400"
            }`}
          >
            {config.tab2Title}
          </span>
        </div>

        {/* Add city button */}
        <button
          onClick={() => setIsAddCityModalOpen(true)}
          className="w-auto border-dashed border-[.8px] lg:border-[.3px] border-shuttlelaneBlack p-1 rounded-sm flex items-center gap-x-1"
        >
          <AiOutlinePlus size={16} />
          <span className="text-xs">Add {config.cityLabel}</span>
        </button>
      </div>

      <div className="w-full">
        {isOverview && (
          <div className="mt-7">
            <div className="W-full h-auto flex gap-x-3 rounded-lg">
              {/* Total Card */}
              <div className="flex flex-row gap-x-2 p-3 items-center rounded-lg border-[.3px] border-gray-100 lg:w-1/4 w-full">
                <div className="h-10 w-10 rounded-full bg-shuttlelanePurple shadow-[#4540cf85] shadow-md flex justify-center items-center">
                  <MdOutlineLocationCity size={16} className="text-white" />
                </div>
                <div className="">
                  {isLoading ? (
                    <ImSpinner2
                      size={20}
                      className="text-shuttlelanePurple animate-spin"
                    />
                  ) : (
                    <p className="text-2xl font-semibold spaceGroteskText">
                      {cities?.length}
                    </p>
                  )}
                  <small className="text-sm text-gray-400">
                    {config.cityLabel}s
                  </small>
                </div>
              </div>
            </div>

            {/* Display cities info here */}
            <div className="mt-5 shuttlelaneScrollbarHoriz overflow-x-scroll">
              {/* Table header */}
              <div className="maxContent lg:max-w-full lg:min-w-full flex justify-between items-baseline mb-2 border-b-[.3px] border-b-gray-100 text-gray-400 mt-2">
                <p className="w-[200px] lg:w-[33.3%] text-xs">
                  {config.cityLabel}
                </p>
                <p className="w-[200px] lg:w-[33.3%] text-xs">
                  {config.associatedEntitiesLabel}
                </p>
                <p className="w-[200px] lg:w-[33.3%] text-xs">Actions</p>
              </div>

              {/* Table body */}
              {!isLoading && (
                <>
                  {cities?.map((city) => (
                    <div
                      key={city._id}
                      className="flex maxContent cursor-pointer lg:max-w-full lg:min-w-full justify-between items-center mb-2 pb-2 border-b-[.3px] border-b-gray-100 text-shuttlelaneBlack mt-4"
                    >
                      <p
                        onClick={() => {
                          setIsUpdateCityModalOpen(true);
                          setCurrentClickedCity(city);
                        }}
                        className={`w-[200px] lg:w-[33.3%] text-xs ${
                          isLoading && "text-gray-400"
                        }`}
                      >
                        {city?.cityName}
                      </p>
                      <div
                        onClick={() => {
                          setIsUpdateCityModalOpen(true);
                          setCurrentClickedCity(city);
                        }}
                        className={`w-[200px] lg:w-[33.3%] text-xs ${
                          isLoading && "text-gray-400"
                        }`}
                      >
                        <ol className="list-decimal">
                          {city?.associatedEntities?.map((entity, index) => (
                            <li key={index} className="text-xs">
                              {entity?.displayName}
                            </li>
                          ))}
                        </ol>
                        {city?.associatedEntities?.length < 1 && (
                          <span className="text-xs">NIL</span>
                        )}
                      </div>

                      <div className="w-[200px] lg:w-[33.3%] flex items-center gap-x-3">
                        {!isLoading ? (
                          <button
                            onClick={() => {
                              handleDeleteCity(city?._id);
                            }}
                            className="text-xs"
                          >
                            <AiFillDelete size={16} className="text-red-500" />
                          </button>
                        ) : (
                          <ImSpinner2
                            size={16}
                            className="text-gray-400 animate-spin"
                          />
                        )}
                      </div>
                    </div>
                  ))}
                </>
              )}

              {cities?.length < 1 && !isLoading && (
                <div className="flex flex-col items-center gap-y-5 text-center mt-10">
                  <p className="text-center text-sm">
                    No {config.cityLabel.toLowerCase()}s to show for now...
                  </p>
                </div>
              )}

              {isLoading && (
                <div className="flex justify-center mt-10">
                  <ImSpinner2
                    size={20}
                    className="text-shuttlelanePurple animate-spin"
                  />
                </div>
              )}
            </div>
          </div>
        )}

        {/* This is the crucial change */}
        {isTab2Active && Tab2ComponentToRender && <Tab2ComponentToRender />}
        {isTab2Active && !Tab2ComponentToRender && (
          <div className="mt-7 text-center text-gray-500">
            No specific component defined for "{config.tab2Title}" for this
            booking type.
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminCitiesLayout;
